// @ts-nocheck
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  FileText, 
  PlusCircle, 
  Clock, 
  CheckCircle, 
  ArrowRight,
  Loader2,
  PieChart,
  TrendingUp,
  Wallet,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  LayoutDashboard,
  AlertTriangle,
  FileX,
  XCircle
} from "lucide-react";

// FIREBASE & CONTEXT
import { collection, query, where, onSnapshot, orderBy } from "firebase/firestore";
import { db } from "../../config/firebase";
import { useAuth } from "../../contexts/AuthContext";

// ============================================================================
// 🚀 TELA PRINCIPAL DA IMOBILIÁRIA (TENANT)
// ============================================================================

const DashboardPage = () => {
  const navigate = useNavigate();
  const { userInfo, companyInfo } = useAuth();
  const brandColor = "#00a99d";
  
// Função para pegar a data de "Hoje" e "30 dias atrás"
  const getTodayString = () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  };

  const getThirtyDaysAgoString = () => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  // Filtro de Data Inicial e Final (Default: Últimos 30 dias para melhor UX)
  const [dateRange, setDateRange] = useState({
    start: getThirtyDaysAgoString(),
    end: getTodayString()
  });

  // Estado para armazenar TODAS as requests da Imobiliária em memória
  const [allRequests, setAllRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  // Estado das métricas calculadas pelo Date Picker
  const [periodStats, setPeriodStats] = useState({
    totalCurrent: 0,
    totalPrev: 0,
    emAnalise: 0,
    aprovadas: 0,
    pendentes: 0,
    rejeitadas: 0,
    volumeAprovado: 0,
    volumeEmAnalise: 0,
    recentes: [] // Guardamos as mais recentes filtradas aqui
  });

  // 🛡️ PARSER RESILIENTE (Resolve o conflito de Dados Legados vs Dados Novos)
  /** @param {any} val */
  const parseCurrency = (val) => {
    if (val === null || val === undefined || val === '') return 0;
    if (typeof val === 'number') return val;
    const strVal = String(val);
    if (strVal.includes(',')) return parseFloat(strVal.replace(/\./g, '').replace(',', '.')) || 0;
    return parseFloat(strVal) || 0;
  };

  // ============================================================================
  // 1. BUSCA GLOBAL DE DADOS (UMA VEZ)
  // ============================================================================
  useEffect(() => {
    if (!companyInfo?.id) return;

    // Busca todas as solicitações desta imobiliária para processar localmente
    const qRequests = query(
      collection(db, "requests"),
      where("companyId", "==", companyInfo.id),
      orderBy("createdAt", "desc")
    );

    const unsubRequests = onSnapshot(qRequests, (snapshot) => {
      const docs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAllRequests(docs);
      setLoading(false);
    });

    return () => unsubRequests();
  }, [companyInfo?.id]);

  // ============================================================================
  // 2. CÁLCULO INTELIGENTE DO DATE PICKER (POR PERÍODO)
  // ============================================================================
  useEffect(() => {
    if (loading) return;

    // Ajuste de fuso horário para comparação precisa
    const startD = new Date(`${dateRange.start}T00:00:00`).getTime();
    const endD = new Date(`${dateRange.end}T23:59:59`).getTime();

    const diffTime = Math.abs(endD - startD);
    const prevEndD = startD - 1;
    const prevStartD = prevEndD - diffTime;

    let tCurrent = 0, tPrev = 0;
    let emAnalise = 0, aprovadas = 0, pendentes = 0, rejeitadas = 0;
    let vAprov = 0, vEmAnalise = 0;
    let filteredRecent = [];

    allRequests.forEach((data) => {
      const statusRaw = data.status || "";
      
      // 🟢 MAPEAMENTO DE STATUS RESILIENTE (Sincronizado com o Banco de Dados)
      const isApproved = statusRaw === "Aprovada" || statusRaw === "Garantia Ativa";
      const isEvaluating = statusRaw === "Em Analise" || statusRaw === "Análise";
      const isPending = statusRaw === "Pendente";
      const isRejected = statusRaw === "Rejeitada" || statusRaw === "Reprovada";

      const reqDate = data.createdAt?.toDate() || data.updatedAt?.toDate();
      if (!reqDate) return;

      const time = reqDate.getTime();

      // É do período ATUAL selecionado?
      if (time >= startD && time <= endD) {
        tCurrent++;
        
        // Pega as 5 mais recentes do período
        if (filteredRecent.length < 5) filteredRecent.push(data);

        // Contagem de Status usando a lógica resiliente
        if (isEvaluating) emAnalise++;
        if (isApproved) aprovadas++;
        if (isPending) pendentes++;
        if (isRejected) rejeitadas++;

        // Soma Financeira do Período Atual (USANDO O PARSER RESILIENTE)
        if (data.imovel?.aluguel) {
          const aluguelNum = parseCurrency(data.imovel.aluguel);
          if (isApproved) vAprov += aluguelNum;
          if (isEvaluating || isPending) vEmAnalise += aluguelNum;
        }
      } 
      // É do período ANTERIOR? (Para a Trend Line)
      else if (time >= prevStartD && time <= prevEndD) {
        tPrev++;
      }
    });

    setPeriodStats({
      totalCurrent: tCurrent,
      totalPrev: tPrev,
      emAnalise,
      aprovadas,
      pendentes,
      rejeitadas,
      volumeAprovado: vAprov,
      volumeEmAnalise: vEmAnalise,
      recentes: filteredRecent
    });

  }, [allRequests, dateRange, loading]);

  // ============================================================================
  // 3. FUNÇÕES DE UX E FORMATAÇÃO
  // ============================================================================
  const renderStatusBadge = (rawStatus) => {
    // 🟢 Blindagem contra espaços invisíveis do Banco de Dados
    const status = (rawStatus || "").trim();
    
    let badgeClass = "bg-slate-50 text-slate-600 border-slate-200"; // Fallback visual
    
    if (status === "Garantia Ativa") {
      badgeClass = "bg-green-500 text-white border-green-600 shadow-sm";
    } else if (status === "Em Analise" || status === "Análise") {
      badgeClass = "bg-blue-500 text-white border-blue-600 shadow-sm";
    } else if (status === "Aprovada" || status === "Aprovado") {
      badgeClass = "bg-white text-black border-black shadow-sm";
    } else if (status === "Pendente") {
      badgeClass = "bg-yellow-50 text-yellow-700 border-yellow-200";
    } else if (status === "Rejeitada" || status === "Reprovada") {
      badgeClass = "bg-red-50 text-red-600 border-red-200";
    } else if (status === "Contrato Encerrado") {
      badgeClass = "bg-slate-50 text-slate-500 border-slate-200 line-through decoration-slate-400";
    }

    return (
      <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wide border ${badgeClass}`}>
        {status || "Desconhecido"}
      </span>
    );
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const getTrendData = (current, prev) => {
    const diff = current - prev;
    if (diff === 0) return { text: "-", color: "text-slate-500 bg-slate-50/50", icon: null };
    if (diff > 0) return { text: `+${diff}`, color: "text-green-600 bg-green-50", icon: <ArrowUpRight className="w-3.5 h-3.5 mr-0.5" /> };
    return { text: `${diff}`, color: "text-red-600 bg-red-50", icon: <ArrowDownRight className="w-3.5 h-3.5 mr-0.5" /> };
  };

  const taxaAprovacao = periodStats.totalCurrent > 0 ? Math.round((periodStats.aprovadas / periodStats.totalCurrent) * 100) : 0;
  const trendTotal = getTrendData(periodStats.totalCurrent, periodStats.totalPrev);

  // ALERTA GLOBAL INDEPENDENTE DO FILTRO DE DATA
  const totalPendentesGlobal = allRequests.filter(r => r.status === "Pendente").length;
  const totalRejeitadasGlobal = allRequests.filter(r => r.status === "Rejeitada" || r.status === "Reprovada").length;

  return (
    <div className="p-4 sm:p-6 lg:p-8 w-full font-sans animate-fade-in bg-[#f8fafc] min-h-full">
      
      {/* HEADER, BEM-VINDO E DATE PICKER + BOTÃO */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-6 border-b border-slate-200/60 pb-6">
        
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-teal-50 flex items-center justify-center shrink-0 border border-teal-100/50 shadow-sm">
            <LayoutDashboard className="w-7 h-7" style={{ color: brandColor }} />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">
              Painel da Imobiliária
            </h1>
            <p className="text-sm font-medium text-slate-500 mt-1">
              Olá, <span className="font-semibold text-teal-600">{userInfo?.name || "Usuário"}</span> • {companyInfo?.nome || "Imobiliária"}
            </p>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto">
          {/* Date Picker */}
          <div className="flex items-center bg-white border border-slate-200/60 rounded-xl px-3 py-2.5 shadow-sm w-full sm:w-auto hover:border-teal-300 transition-colors">
            <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
            <input 
              type="date" 
              value={dateRange.start}
              max={dateRange.end}
              onChange={(e) => setDateRange(prev => ({...prev, start: e.target.value}))}
              className="text-xs sm:text-sm font-medium text-slate-600 bg-transparent focus:outline-none cursor-pointer mx-2 w-full sm:w-auto"
            />
            <span className="text-slate-300 text-xs sm:text-sm">até</span>
            <input 
              type="date" 
              value={dateRange.end}
              min={dateRange.start}
              onChange={(e) => setDateRange(prev => ({...prev, end: e.target.value}))}
              className="text-xs sm:text-sm font-medium text-slate-600 bg-transparent focus:outline-none cursor-pointer ml-2 w-full sm:w-auto"
            />
          </div>

          {/* Botão de Nova Solicitação */}
          <button 
            style={{ backgroundColor: brandColor }}
            className="flex justify-center items-center w-full sm:w-auto px-6 py-2.5 text-white rounded-xl text-sm font-semibold hover:brightness-110 transition-all shadow-md shadow-teal-500/20 shrink-0"
            onClick={() => navigate('/dashboard/novo-request')}
          >
            <PlusCircle className="w-4 h-4 mr-2" /> Nova Ficha
          </button>
        </div>

      </div>

      {/* ============================================================================
          🚀 ALERTAS GLOBAIS DE AÇÃO (UI/UX - PADRÃO FIFA)
          ============================================================================ */}
      {(totalPendentesGlobal > 0 || totalRejeitadasGlobal > 0) && (
        <div className="flex flex-col gap-3 mb-8 animate-fade-in">
          {totalPendentesGlobal > 0 && (
            <div className="flex items-start sm:items-center gap-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl shadow-sm">
              <div className="p-2 bg-yellow-100 rounded-full shrink-0 shadow-sm border border-yellow-200">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-bold text-yellow-800">Ação Necessária: Fichas com Pendência</h4>
                <p className="text-xs text-yellow-700 mt-0.5 font-medium">
                  Existem <strong className="font-bold">{totalPendentesGlobal}</strong> solicitação(ões) precisando da sua atenção para envio de documentos, correções ou assinaturas.
                </p>
              </div>
              <button
                onClick={() => navigate('/dashboard/requests')}
                className="px-4 py-2 bg-yellow-600 text-white text-xs font-bold rounded-lg hover:bg-yellow-700 transition-colors shadow-sm whitespace-nowrap shrink-0"
              >
                Resolver Agora
              </button>
            </div>
          )}

          {totalRejeitadasGlobal > 0 && (
            <div className="flex items-start sm:items-center gap-4 p-4 bg-red-50 border border-red-200 rounded-xl shadow-sm">
              <div className="p-2 bg-red-100 rounded-full shrink-0 shadow-sm border border-red-200">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-bold text-red-800">Atenção: Fichas Reprovadas</h4>
                <p className="text-xs text-red-700 mt-0.5 font-medium">
                  Temos <strong className="font-bold">{totalRejeitadasGlobal}</strong> ficha(s) que não foram aprovadas pela análise de risco.
                </p>
              </div>
              <button
                onClick={() => navigate('/dashboard/requests')}
                className="px-4 py-2 bg-red-600 text-white text-xs font-bold rounded-lg hover:bg-red-700 transition-colors shadow-sm whitespace-nowrap shrink-0"
              >
                Ver Detalhes
              </button>
            </div>
          )}
        </div>
      )}

      {/* SEÇÃO DE CARDS SUPERIORES (5 Colunas) */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-5 mb-8">
        
        {/* CARD TOTAL DE SOLICITAÇÕES COM TREND */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200/60 shadow-sm flex flex-col justify-between hover:shadow-md transition-shadow group">
          <div className="flex justify-between items-start">
            <div className="p-2.5 bg-slate-50 rounded-xl group-hover:scale-110 transition-transform">
              <FileText className="w-5 h-5 text-slate-600" />
            </div>
            {trendTotal.text !== "-" && (
              <span className={`flex items-center text-[11px] font-semibold px-2.5 py-1 rounded-lg ${trendTotal.color} tracking-wide`}>
                {trendTotal.icon} {trendTotal.text}
              </span>
            )}
          </div>
          <div className="mt-4">
            <p className="text-xs font-medium text-slate-500 mb-1">Total (Período)</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-700">{periodStats.totalCurrent}</h2>
          </div>
        </div>

        {/* CARDS DE STATUS */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200/60 shadow-sm flex flex-col justify-between hover:shadow-md transition-shadow group">
          <div className="flex justify-between items-start">
            <div className="p-2.5 bg-blue-50 rounded-xl group-hover:scale-110 transition-transform">
              <Clock className="w-5 h-5 text-blue-500" />
            </div>
          </div>
          <div className="mt-4">
            <p className="text-xs font-medium text-slate-500 mb-1">Em Análise</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-700">{periodStats.emAnalise}</h2>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200/60 shadow-sm flex flex-col justify-between hover:shadow-md transition-shadow group">
          <div className="flex justify-between items-start">
            <div className="p-2.5 bg-green-50 rounded-xl group-hover:scale-110 transition-transform">
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
          </div>
          <div className="mt-4">
            <p className="text-xs font-medium text-slate-500 mb-1">Aprovadas</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-700">{periodStats.aprovadas}</h2>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200/60 shadow-sm flex flex-col justify-between hover:shadow-md transition-shadow group">
          <div className="flex justify-between items-start">
            <div className="p-2.5 bg-yellow-50 rounded-xl group-hover:scale-110 transition-transform">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
            </div>
          </div>
          <div className="mt-4">
            <p className="text-xs font-medium text-slate-500 mb-1">Pendentes</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-700">{periodStats.pendentes}</h2>
          </div>
        </div>

        {/* CARD REJEITADAS */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200/60 shadow-sm flex flex-col justify-between hover:shadow-md transition-shadow group">
          <div className="flex justify-between items-start">
            <div className="p-2.5 bg-red-50 rounded-xl group-hover:scale-110 transition-transform">
              <XCircle className="w-5 h-5 text-red-500" />
            </div>
          </div>
          <div className="mt-4">
            <p className="text-xs font-medium text-slate-500 mb-1">Rejeitadas</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-700">{periodStats.rejeitadas}</h2>
          </div>
        </div>

      </div>

      {/* GRID INFERIOR: TABELA E ANALYTICS */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        
        {/* TABELA DE SOLICITAÇÕES RECENTES (2/3) */}
        <div className="xl:col-span-2 bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden flex flex-col h-full hover:shadow-md transition-shadow">
          <div className="p-6 border-b border-slate-200/60 flex flex-col sm:flex-row justify-between items-center gap-4 bg-slate-50/50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-50 rounded-lg border border-teal-100/50">
                <FileText className="w-4 h-4" style={{ color: brandColor }} />
              </div>
              <h3 className="font-semibold text-slate-700">Destaques do Período</h3>
            </div>
            <button onClick={() => navigate('/dashboard/requests')} className="text-[13px] font-semibold text-slate-500 hover:text-teal-600 transition-colors flex items-center gap-1 bg-white px-3 py-1.5 rounded-lg border border-slate-200 shadow-sm">
              Ver todas <ArrowRight className="w-3 h-3" />
            </button>
          </div>

          <div className="overflow-x-auto flex-1">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="text-[11px] uppercase font-semibold text-slate-500 tracking-wider border-b border-slate-200/60 bg-slate-50/80">
                  <th className="px-6 py-4">Solicitação</th>
                  <th className="px-6 py-4">Cód. Imóvel</th>
                  <th className="px-6 py-4">Data</th>
                  <th className="px-6 py-4 text-center">Status</th>
                  <th className="px-6 py-4 text-right">Ação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100/80">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="py-20 text-center">
                      <Loader2 className="w-8 h-8 text-teal-500 animate-spin mx-auto" />
                      <p className="text-xs text-slate-500 mt-3 font-medium">Processando dados...</p>
                    </td>
                  </tr>
                ) : periodStats.recentes.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-20 text-center">
                       <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3">
                         <FileX className="w-5 h-5 text-slate-300" />
                       </div>
                       <p className="text-slate-500 text-sm font-medium">Nenhuma solicitação encontrada no período.</p>
                    </td>
                  </tr>
                ) : (
                  periodStats.recentes.map((req) => (
                    <tr key={req.id} className="hover:bg-slate-50/60 transition-colors group cursor-pointer" onClick={() => navigate(`/dashboard/request/${(req.protocolo || req.numeroSolicitacao || req.id).replace('/','-')}`)}>
                      <td className="px-6 py-4 font-semibold text-[#00a99d] text-[13px] xl:text-sm tracking-tight">{req.protocolo || req.numeroSolicitacao || "S/N"}</td>
                      <td className="px-6 py-4 text-slate-600 text-[13px] xl:text-sm font-medium">{req.imovel?.codigo || "-"}</td>
                      <td className="px-6 py-4 text-slate-500 text-[12px] xl:text-[13px] font-medium">{req.dataSolicitacaoTexto?.split(' às')[0] || "-"}</td>
                      <td className="px-6 py-4 text-center">{renderStatusBadge(req.status)}</td>
                      <td className="px-6 py-4 text-right">
                        <div className="p-2 rounded-xl bg-slate-50 text-slate-400 group-hover:bg-teal-50 group-hover:text-teal-600 transition-colors inline-flex">
                          <ArrowRight className="w-4 h-4" />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ANALYTICS FINANCEIRO & TAXA DE APROVAÇÃO (1/3) */}
        <div className="xl:col-span-1 flex flex-col gap-6">
          
          {/* TAXA DE APROVAÇÃO */}
          <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-6 hover:shadow-md transition-shadow">
            <h3 className="font-semibold text-slate-700 flex items-center gap-2 mb-6">
              <div className="p-2 bg-teal-50 rounded-lg border border-teal-100/50"><PieChart className="w-4 h-4" style={{ color: brandColor }} /></div>
              Aprovação (Período)
            </h3>
            
            <div className="flex flex-col items-center justify-center py-2">
              <div className="relative w-36 h-36 flex items-center justify-center">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                  <path className="text-slate-100" strokeWidth="3.5" stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                  <path className="text-teal-500 transition-all duration-1000 ease-out" strokeDasharray={`${taxaAprovacao}, 100`} strokeWidth="3.5" strokeLinecap="round" stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                </svg>
                <div className="absolute flex flex-col items-center justify-center text-center">
                  <span className="text-3xl font-bold text-slate-700">{taxaAprovacao}%</span>
                </div>
              </div>
              <p className="text-sm font-medium text-slate-500 mt-6 text-center px-2 leading-relaxed">
                De <strong className="text-slate-700 mx-1">{periodStats.totalCurrent}</strong> envios, <strong className="text-teal-600 mx-1">{periodStats.aprovadas}</strong> foram aprovados neste período.
              </p>
            </div>
          </div>

          {/* VOLUME FINANCEIRO */}
          <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-6 flex flex-col flex-1 hover:shadow-md transition-shadow">
            <h3 className="font-semibold text-slate-700 flex items-center gap-2 mb-4">
              <div className="p-2 bg-teal-50 rounded-lg border border-teal-100/50"><TrendingUp className="w-4 h-4" style={{ color: brandColor }} /></div>
              Volume (Período)
            </h3>
            
            <div className="flex flex-col justify-center flex-1 gap-4 mt-2">
              <div className="flex items-center gap-4 bg-green-50/50 p-4 rounded-xl border border-green-100/50">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center shrink-0 shadow-sm">
                  <Wallet className="w-5 h-5 text-green-600" />
                </div>
                <div className="flex flex-col justify-center">
                  <p className="text-xs font-medium text-green-700/80 mb-0.5">Aprovados</p>
                  <p className="text-xl font-semibold text-slate-700 leading-tight">{formatCurrency(periodStats.volumeAprovado)}</p>
                </div>
              </div>

              <div className="flex items-center gap-4 bg-blue-50/50 p-4 rounded-xl border border-blue-100/50">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center shrink-0 shadow-sm">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex flex-col justify-center">
                  <p className="text-xs font-medium text-blue-700/80 mb-0.5">Em Avaliação</p>
                  <p className="text-xl font-semibold text-slate-700 leading-tight">{formatCurrency(periodStats.volumeEmAnalise)}</p>
                </div>
              </div>
            </div>

          </div>
        </div>

      </div>

    </div>
  );
};

export default DashboardPage;